import React from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonModal,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { ChannelClassification } from '@trycourier/react-hooks/typings/preferences/types';
import { useFirebase } from '../../../../services/firebase';
import useIonicModalProps from '../../../../hooks/useIonicModalProps';
import styled from 'styled-components';
import Typography from '../../../common/Typography';
import { isWeb } from '../../../../util/device';
import {
  COURIER_CASE_NOTIF_PREFERENCE_ID,
  COURIER_MESSAGE_NOTIF_PREFERENCE_ID,
} from '../../../../util/courier';

interface ChannelPreferenceProps {
  templateId: string;
  toggled: boolean;
  channel: ChannelClassification;
  onChange: (toggled: boolean) => void;
  topicDisabled: boolean;
}

const NAME_FOR_CHANNEL = {
  ['sms']: 'SMS',
  ['email']: 'Email',
  ['push']: 'Push',
};

export const isChannelRequired = (
  templateId: string,
  channel: 'sms' | 'email' | 'push',
  isAdmin: boolean
): boolean => {
  if (channel !== 'email') {
    return false;
  }
  if (templateId === COURIER_CASE_NOTIF_PREFERENCE_ID) {
    return true;
  }
  // Admins keep the opt-in Courier message email; clients get an
  // unconditional one sent outside the preference system.
  return templateId === COURIER_MESSAGE_NOTIF_PREFERENCE_ID && !isAdmin;
};

const REQUIRED_EMAIL_HELPER_TEXT: Record<string, string> = {
  [COURIER_CASE_NOTIF_PREFERENCE_ID]: 'Case report emails cannot be turned off',
  [COURIER_MESSAGE_NOTIF_PREFERENCE_ID]:
    'Message notification emails cannot be turned off',
};

const EmailChannelPreference: React.FC<ChannelPreferenceProps> = ({
  toggled,
  onChange,
  topicDisabled,
  templateId,
}) => {
  const { currentUser, isAdmin } = useFirebase();
  const hasNoEmail = !currentUser?.email;
  const isRequired =
    isChannelRequired(templateId, 'email', isAdmin) && !hasNoEmail;
  return (
    <IonItem>
      <IonToggle
        className={hasNoEmail ? 'ion-invalid ion-touched' : ''}
        disabled={topicDisabled || hasNoEmail || isRequired}
        checked={toggled || isRequired}
        onIonChange={(e) => onChange(e.detail.checked)}
        errorText={'Must have email address attached to account'}
        helperText={
          isRequired ? REQUIRED_EMAIL_HELPER_TEXT[templateId] : undefined
        }
      >
        Email
      </IonToggle>
    </IonItem>
  );
};

const SMSOptInModalButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
`;
const SMSOptInModalContent = styled(IonContent)`
  height: 100%;
  display: flex;
  &::part(scroll) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  flex-direction: column;
  justify-content: space-between;
`;
const SMSOptInModalTextContainer = styled.div`
  padding: 32px 16px;
`;

const SMSChannelPreference: React.FC<ChannelPreferenceProps> = ({
  toggled,
  onChange,
  topicDisabled,
}) => {
  const { currentUser } = useFirebase();
  const hasNoPhone = !currentUser?.phoneNumber;
  const [showOptInModal, setShowOptInModal] = React.useState(false);
  const modalProps = useIonicModalProps();
  const toggleRef = React.useRef<HTMLIonToggleElement>(null);
  const onModalClose = () => {
    setShowOptInModal(false);
    modalProps.onClose();
  };

  return (
    <>
      <IonItem>
        <IonToggle
          ref={toggleRef}
          className={hasNoPhone ? 'ion-invalid ion-touched' : ''}
          disabled={topicDisabled || hasNoPhone}
          checked={toggled}
          onIonChange={(e) => {
            if (e.detail.checked) {
              setShowOptInModal(true);
            } else {
              onChange(e.detail.checked);
            }
          }}
          errorText={'Must have phone number attached to account'}
          helperText="Data and messaging rates may apply"
        >
          SMS
        </IonToggle>
      </IonItem>

      <IonModal isOpen={showOptInModal} onDidDismiss={onModalClose}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Opt-in to SMS Notifications</IonTitle>
            <IonButtons slot="end">
              <IonButton title="Close" onClick={onModalClose}>
                Close
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <SMSOptInModalContent>
          <SMSOptInModalTextContainer>
            <Typography lineHeight="1.8em" margin="16px 0">
              By clicking "Confirm" below, you agree to receive SMS
              notifications from Pocket Pathologist, LLC. Message and data rates
              may apply. You can opt-out at any time by changing your
              notification preferences on this page.
            </Typography>
          </SMSOptInModalTextContainer>

          <SMSOptInModalButtonContainer>
            <IonButton
              fill="clear"
              onClick={() => {
                onModalClose();
                if (toggleRef.current) {
                  toggleRef.current.checked = false;
                }
              }}
            >
              Cancel
            </IonButton>
            <IonButton
              fill="solid"
              expand="block"
              onClick={() => {
                onChange(true);
                onModalClose();
              }}
            >
              Confirm
            </IonButton>
          </SMSOptInModalButtonContainer>
        </SMSOptInModalContent>
      </IonModal>
    </>
  );
};

const PushChannelPreference: React.FC<ChannelPreferenceProps> = ({
  toggled,
  onChange,
  topicDisabled,
}) => {
  const toggleRef = React.useRef<HTMLIonToggleElement>(null);
  const isNativeWeb = isWeb();
  const permissionDenied = isNativeWeb
    ? Notification.permission === 'denied'
    : false;

  const requestPermissionWeb = () => {
    Notification.requestPermission()
      .then((permission) => {
        console.log('Push permission:', permission);
        if (permission === 'granted') {
          onChange(true);
        } else {
          console.warn('Push notifications are not enabled');
          if (toggleRef.current) {
            toggleRef.current.checked = false;
          }
          onChange(false);
        }
      })
      .catch(() => {
        if (toggleRef.current) {
          toggleRef.current.checked = false;
        }
      });
  };

  const requestPermissionNative = () => {
    onChange(true);
  };
  return (
    <IonItem>
      <IonToggle
        ref={toggleRef}
        className={permissionDenied ? 'ion-invalid ion-touched' : ''}
        disabled={topicDisabled || permissionDenied}
        checked={toggled && !permissionDenied}
        onIonChange={(e) => {
          if (!e.detail.checked) {
            onChange(e.detail.checked);
          } else {
            if (toggleRef.current) {
              toggleRef.current.checked = false;
            }

            if (isNativeWeb) {
              requestPermissionWeb();
            } else {
              requestPermissionNative();
            }
          }
        }}
        errorText={'Push notifications blocked for this site'}
      >
        Push
      </IonToggle>
    </IonItem>
  );
};

const CHANNEL_COMPONENT_MAP = {
  email: EmailChannelPreference,
  sms: SMSChannelPreference,
  push: PushChannelPreference,
};

const ChannelPreference: React.FC<ChannelPreferenceProps> = ({
  toggled,
  channel,
  onChange,
  topicDisabled,
  templateId,
}) => {
  const Component = CHANNEL_COMPONENT_MAP[channel];
  if (Component) {
    return (
      <Component
        toggled={toggled}
        channel={channel}
        onChange={onChange}
        topicDisabled={topicDisabled}
        templateId={templateId}
      />
    );
  }

  return (
    <IonItem>
      <IonToggle
        disabled={topicDisabled}
        checked={toggled}
        onIonChange={(e) => onChange(e.detail.checked)}
      >
        {NAME_FOR_CHANNEL[channel] || channel}
      </IonToggle>
    </IonItem>
  );
};

export default ChannelPreference;
